import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Minus, Plus, Route, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export interface AtlasConfig {
  plan: "small" | "medium" | "medium3";
  adminUsers: number;
  viewerUsers: number;
  maps: number;
  countries: string[];
  routing: "none" | "small" | "medium" | "large";
}

interface Props {
  config: AtlasConfig;
  onChange: (config: AtlasConfig) => void;
}

const PLANS = ["small", "medium", "medium3"] as const;
const PLAN_BINDING: Record<string, string> = {
  small: "order.binding12",
  medium: "order.binding12",
  medium3: "order.binding3",
};
const COUNTRIES = [
  { id: "se", labelKey: "order.countries.se" },
  { id: "no", labelKey: "order.countries.no" },
  { id: "dk", labelKey: "order.countries.dk" },
  { id: "fi", labelKey: "order.countries.fi" },
];

export function OrderAtlasConfig({ config, onChange }: Props) {
  const { t } = useTranslation();

  const handleCountryToggle = (countryId: string, checked: boolean) => {
    const updated = checked
      ? [...config.countries, countryId]
      : config.countries.filter((c) => c !== countryId);
    // Ensure at least 1 country
    if (updated.length > 0) {
      onChange({ ...config, countries: updated });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">
          {t("order.configureAtlas")}
        </h2>
        <p className="text-muted-foreground mt-1">
          {t("order.configureAtlasDesc")}
        </p>
      </div>

      {/* Plan selection */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t("order.choosePlan")}</Label>
        <div className="grid sm:grid-cols-3 gap-4">
          {PLANS.map((plan) => (
            <button
              key={plan}
              onClick={() => onChange({ ...config, plan })}
              className={cn(
                "flex flex-col items-center p-5 rounded-xl border-2 transition-all",
                config.plan === plan
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/50"
              )}
            >
              <span className="text-sm font-bold text-foreground">
                {t(`productPages.atlas.pricing.plans.${plan}.name`)}
              </span>
              <span className="text-lg font-semibold text-primary mt-1">
                {t(`productPages.atlas.pricing.plans.${plan}.price`)}
              </span>
              <span className="text-xs text-muted-foreground">{t("order.perMonth")}</span>
              <span className="text-[10px] text-muted-foreground mt-1">{t(PLAN_BINDING[plan])}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Users & Maps */}
      <div className="grid sm:grid-cols-3 gap-6">
        <div className="space-y-2">
          <Label>{t("order.adminUsers")}</Label>
          <p className="text-xs text-muted-foreground">{t("order.adminUsersDesc")}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => onChange({ ...config, adminUsers: Math.max(1, config.adminUsers - 1) })}
              disabled={config.adminUsers <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-10 text-center text-lg font-semibold tabular-nums">{config.adminUsers}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => onChange({ ...config, adminUsers: config.adminUsers + 1 })}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("order.viewerUsers")}</Label>
          <p className="text-xs text-muted-foreground">{t("order.viewerUsersDesc")}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => onChange({ ...config, viewerUsers: Math.max(0, config.viewerUsers - 1) })}
              disabled={config.viewerUsers <= 0}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-10 text-center text-lg font-semibold tabular-nums">{config.viewerUsers}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => onChange({ ...config, viewerUsers: config.viewerUsers + 1 })}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("order.maps")}</Label>
          <p className="text-xs text-muted-foreground">{t("order.mapsDesc")}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => onChange({ ...config, maps: Math.max(1, config.maps - 1) })}
              disabled={config.maps <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-10 text-center text-lg font-semibold tabular-nums">{config.maps}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => onChange({ ...config, maps: config.maps + 1 })}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Countries */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t("order.countries.title")}</Label>
        <p className="text-xs text-muted-foreground">{t("order.countries.desc")}</p>
        <div className="flex flex-wrap gap-4">
          {COUNTRIES.map((country) => (
            <label
              key={country.id}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all",
                config.countries.includes(country.id)
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
            >
              <Checkbox
                checked={config.countries.includes(country.id)}
                onCheckedChange={(checked) =>
                  handleCountryToggle(country.id, checked === true)
                }
                disabled={
                  config.countries.length === 1 &&
                  config.countries.includes(country.id)
                }
              />
              <span className="text-sm font-medium">{t(country.labelKey)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Routing add-on */}
      <RoutingAddon
        plan={config.plan}
        routing={config.routing}
        onChange={(routing) => onChange({ ...config, routing })}
      />
    </div>
  );
}

const ROUTING_TIERS = ["none", "small", "medium", "large"] as const;

const ROUTING_PRICES: Record<string, number> = {
  none: 0,
  small: 495,
  medium: 995,
  large: 1495,
};

function RoutingAddon({
  plan,
  routing,
  onChange,
}: {
  plan: string;
  routing: string;
  onChange: (val: "none" | "small" | "medium" | "large") => void;
}) {
  const { t } = useTranslation();
  const includedInMedium = plan === "medium" || plan === "medium3";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Route className="h-5 w-5 text-primary" />
        <Label className="text-base font-semibold">{t("order.routing.title")}</Label>
      </div>
      <p className="text-xs text-muted-foreground">{t("order.routing.desc")}</p>

      {includedInMedium && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-foreground">{t("order.routing.includedNote")}</p>
        </div>
      )}

      <div className="grid sm:grid-cols-4 gap-3">
        {ROUTING_TIERS.map((tier) => {
          const isIncluded = includedInMedium && tier === "small";
          const fullPrice = ROUTING_PRICES[tier];
          const effectivePrice = tier === "none"
            ? 0
            : includedInMedium
              ? Math.max(0, fullPrice - ROUTING_PRICES.small)
              : fullPrice;
          const hasDiscount = includedInMedium && tier !== "none" && tier !== "small" && effectivePrice < fullPrice;

          let priceLabel: React.ReactNode;
          if (tier === "none") {
            priceLabel = <span className="text-xs font-semibold text-primary">{t("order.routing.nonePrice")}</span>;
          } else if (isIncluded) {
            priceLabel = <span className="text-xs font-semibold text-primary">{t("order.routing.included")}</span>;
          } else if (hasDiscount) {
            priceLabel = (
              <span className="text-xs font-semibold text-primary">
                <span className="line-through text-muted-foreground font-normal mr-1">
                  {fullPrice.toLocaleString("sv-SE")}
                </span>
                {effectivePrice.toLocaleString("sv-SE")} SEK/{t("order.perMonthShort")}
              </span>
            );
          } else {
            priceLabel = (
              <span className="text-xs font-semibold text-primary">
                {fullPrice.toLocaleString("sv-SE")} SEK/{t("order.perMonthShort")}
              </span>
            );
          }

          return (
            <button
              key={tier}
              onClick={() => onChange(tier)}
              className={cn(
                "flex flex-col items-center p-4 rounded-xl border-2 transition-all text-center",
                routing === tier
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/50"
              )}
            >
              <span className="text-sm font-bold text-foreground">
                {t(`order.routing.tiers.${tier}.name`)}
              </span>
              <span className="mt-1">{priceLabel}</span>
              <span className="text-[10px] text-muted-foreground mt-2 leading-tight">
                {t(`order.routing.tiers.${tier}.short`)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
